import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../feature-name";
import {
  ActivationSignalClip,
  ActivationSignalProcessing,
  ListFleetActivationsQueryParams,
  ListTenantActivationsQueryParams,
  MatchedClientNos,
} from "./activations";

/**
 * Patriot signals — the events Patriot received and recorded — listed at
 * whichever level of a tenant the caller is looking at.
 *
 * The signal list is the activation list's upstream: a signal appears here
 * whether or not it raised an activation, so comparing the two shows which
 * signals Patriot received but raised nothing for.
 *
 * Only signals carrying an a3s marker can be placed in a tenant, so only those
 * are listed.
 *
 * Same **allowlist** discipline as `activations.ts`: a field reaches a caller
 * only by being named here. A list row carries what tells signals apart at a
 * glance; the detail carries the rest of what Patriot holds on the signal,
 * addressing and device-state fields included, because it is for investigating
 * one signal.
 *
 * `signalId` is Patriot's own id for the signal — the only one every signal
 * has, so it is what the detail routes are keyed on. It is not the a3s id,
 * which is parsed from the marker in `notes` and absent when a3s did not send
 * the signal.
 */

/**
 * One row of a signal list.
 *
 * Every row carries an a3s marker — the list reads the a3s axes, which hold
 * nothing else — so the placement is required here, where on an activation
 * it is not.
 */
export const SignalSummary = z.object({
  /** Patriot's id for the signal; the detail routes take it. */
  signalId: z.string(),
  /** When Patriot received the signal, normalised to UTC. */
  receivedDateTime: z.string(),
  /** Patriot's description line, e.g. `Vehicle Detected`. */
  description: z.string().optional(),
  /** Patriot's event type, e.g. `130`. */
  typeNo: z.int().optional(),
  /** Patriot's client number, e.g. `10539BASE01`. */
  clientNo: z.string().optional(),
  /**
   * The activation this signal raised or joined. Absent when it raised none,
   * which is always the case for a test signal.
   */
  ticketNo: z.string().optional(),
  a3sSite: z.string(),
  a3sAsset: z.string(),
  a3sCamera: z.string(),
});
export type SignalSummary = z.output<typeof SignalSummary>;

export const ListTenantSignalsResponse = z.object({
  items: z.array(SignalSummary),
  /** Opaque resume token; `null` when the list is exhausted. Pass back verbatim. */
  cursor: z.string().nullable(),
});
export type ListTenantSignalsResponse = z.output<typeof ListTenantSignalsResponse>;

/**
 * The activation list's filters, unchanged. The signal axes mirror the
 * activation ones partition for partition, so the same combinations are
 * servable and the same one — a camera with no site — is refused. `since` /
 * `until` bound the signal's `receivedDateTime`.
 */
export const ListTenantSignalsQueryParams = ListTenantActivationsQueryParams;
export type ListTenantSignalsQueryParamsWire = z.input<typeof ListTenantSignalsQueryParams>;
export type ListTenantSignalsQueryParams = z.output<typeof ListTenantSignalsQueryParams>;

/**
 * ```
 * /features/monitoring/patriot/tenant/signals[?site=&camera=&since=&until=]
 * ```
 *
 * Beside `patriot/tenant/activations`, for the reasons given on
 * `tenantActivationsResource`. A row opens on `tenantSignalResource` by its
 * `signalId`.
 */
export const tenantSignalsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/signals",
  scope: "tenant",
});

export const listTenantSignalsEndpoint = defineEndpoint(tenantSignalsResource, {
  responseSchema: ListTenantSignalsResponse,
  querySchema: ListTenantSignalsQueryParams,
});

/**
 * One row of the fleet signal list: any signal Patriot received, whoever sent
 * it. The a3s placement is left out — its ids are tenant-scoped, and the fleet
 * list spans tenants — so a row says which client the signal came in on
 * instead.
 *
 * Log-on signals are not listed unfiltered; see the fleet axis in
 * `patriot-data-service/schema.ts`. Filtered by client they are, so asking for
 * the log-on client shows them.
 */
export const FleetSignalSummary = z.object({
  /** Patriot's id for the signal; the detail routes take it. */
  signalId: z.string(),
  /** When Patriot received the signal, normalised to UTC. */
  receivedDateTime: z.string(),
  /** Patriot's description line, e.g. `Vehicle Detected`. */
  description: z.string().optional(),
  /** Patriot's event type, e.g. `130`. */
  typeNo: z.int().optional(),
  /** Patriot's client number, e.g. `10539BASE01`. Absent on a signal that names no client. */
  clientNo: z.string().optional(),
  /** The activation this signal raised or joined. Absent when it raised none. */
  ticketNo: z.string().optional(),
});
export type FleetSignalSummary = z.output<typeof FleetSignalSummary>;

export const ListFleetSignalsResponse = z.object({
  items: z.array(FleetSignalSummary),
  /**
   * Opaque resume token; `null` when the window is exhausted. Pass back
   * verbatim. Unfiltered, a page can come back empty with a non-null cursor,
   * as the fleet activations can — page until this is `null`.
   */
  cursor: z.string().nullable(),
  clientNos: MatchedClientNos,
});
export type ListFleetSignalsResponse = z.output<typeof ListFleetSignalsResponse>;

/** The fleet activations' filters, unchanged; `since` / `until` bound `receivedDateTime`. */
export const ListFleetSignalsQueryParams = ListFleetActivationsQueryParams;
export type ListFleetSignalsQueryParamsWire = z.input<typeof ListFleetSignalsQueryParams>;
export type ListFleetSignalsQueryParams = z.output<typeof ListFleetSignalsQueryParams>;

/** Under the caller's tenant in the path only because every client-API route is; see `fleetActivationsResource`. */
export const fleetSignalsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/signals",
  scope: "tenant",
});

export const listFleetSignalsEndpoint = defineEndpoint(fleetSignalsResource, {
  responseSchema: ListFleetSignalsResponse,
  querySchema: ListFleetSignalsQueryParams,
});

/**
 * One signal in full: what Patriot holds on it, and — when a3s sent it and this
 * realm holds the record — the clip and how a3s handled it.
 *
 * `notes` is Patriot's free text on the signal, verbatim, as on
 * `ActivationSignal`: the a3s marker on an a3s signal, whatever another
 * integration wrote on its own. The `a3s*` placement is absent on a signal with
 * no marker, which only the fleet route serves.
 *
 * The rest are Patriot's addressing and device-state fields, passed through
 * as Patriot holds them, for matching a signal against the sender's own logs:
 * `moduleNo` / `zoneNumber` / `userNumber` say where on the
 * client it came in, `rawData` is the message as received, and
 * `currentStatus` / `previousStatus` are the zone's state either side of it.
 *
 * `a3sFirstSeenAt` / `a3sLastWrittenAt` are when the replica first copied the
 * signal and last copied a change to it, as on `ClientDetail`: the gap from
 * `receivedDateTime` to the first is the replica's lag, and a later second
 * means Patriot changed the signal after it came in.
 */
export const SignalDetail = SignalSummary.partial({ a3sSite: true, a3sAsset: true, a3sCamera: true }).extend({
  /** The a3s `SignalId` from the marker. Absent when a3s did not send the signal. */
  a3sSignalId: z.string().optional(),
  notes: z.string().optional(),
  alarmType: z.string().optional(),
  source: z.string().optional(),
  moduleNo: z.int().optional(),
  zoneNumber: z.int().optional(),
  userNumber: z.int().optional(),
  rawData: z.string().optional(),
  currentStatus: z.string().optional(),
  previousStatus: z.string().optional(),
  media: z.string().optional(),
  a3sClip: ActivationSignalClip.optional(),
  a3sProcessing: ActivationSignalProcessing.optional(),
  /** Patriot's name for `clientNo`. Absent when the replica holds no such client. */
  clientName: z.string().optional(),
  a3sFirstSeenAt: z.string().optional(),
  a3sLastWrittenAt: z.string().optional(),
});
export type SignalDetail = z.output<typeof SignalDetail>;

export const GetSignalPathParams = z.object({ signalId: z.string().min(1) });
export type GetSignalPathParams = z.output<typeof GetSignalPathParams>;

/**
 * A signal by Patriot's id, served only when its marker names the tenant in the
 * path prefix — the isolation `tenantActivationResource` describes, for the same
 * reason: the id is a guessable integer.
 */
export const tenantSignalResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/signals/{signalId}",
  scope: "tenant",
  resourcePathSchema: GetSignalPathParams,
});

export const getTenantSignalEndpoint = defineEndpoint(tenantSignalResource, {
  responseSchema: SignalDetail,
});

/** {@link SignalDetail} plus the tenant its marker names, as `FleetActivationDetail`. */
export const FleetSignalDetail = SignalDetail.extend({
  a3sTenant: z.string().optional(),
});
export type FleetSignalDetail = z.output<typeof FleetSignalDetail>;

/** Any signal in the replica, marked or not, with no tenant check — as `fleetActivationResource`. */
export const fleetSignalResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/signals/{signalId}",
  scope: "tenant",
  resourcePathSchema: GetSignalPathParams,
});

export const getFleetSignalEndpoint = defineEndpoint(fleetSignalResource, {
  responseSchema: FleetSignalDetail,
});
